import { color, font } from '../tokens';
import type { Navigate } from '../lib/router';

/** Public information about the data Scribbleplot uses. */
export function PrivacyScreen({ accent, navigate }: { accent: string; navigate: Navigate }) {
  const section = { marginTop: 34 } as const;
  const heading = { fontSize: 17, fontWeight: 700, margin: '0 0 9px' } as const;
  const paragraph = { margin: '0 0 12px', color: color.textMuted, lineHeight: 1.7 } as const;

  return (
    <main style={{ height: '100%', overflowY: 'auto', padding: '48px 24px 80px' }}>
      <article style={{ maxWidth: 720, margin: '0 auto', fontSize: 14 }}>
        <a
          href="/"
          onClick={(event) => {
            event.preventDefault();
            navigate({ name: 'landing' });
          }}
          style={{ color: accent, fontWeight: 700 }}
        >
          ← Scribbleplot
        </a>
        <p
          style={{
            color: color.textFaint,
            fontFamily: font.mono,
            fontSize: 11,
            letterSpacing: '.08em',
            margin: '42px 0 10px',
          }}
        >
          PRIVACY
        </p>
        <h1 style={{ fontSize: 34, lineHeight: 1.15, margin: 0 }}>Privacy policy</h1>
        <p style={{ ...paragraph, marginTop: 10 }}>Effective September 22, 2026</p>

        <section style={section}>
          <h2 style={heading}>What we collect</h2>
          <p style={paragraph}>
            Scribbleplot stores your account email and display name, boards you create, comments, invited collaborators,
            and images you upload. If you sign in with Google, Google supplies a verified email address, name, and a
            stable account identifier. Scribbleplot does not receive your Google password or access your Gmail or Drive
            data.
          </p>
          <p style={paragraph}>
            We also process technical information needed to run the service, such as your IP address, browser
            information, session cookies, and sign-in attempts.
          </p>
        </section>

        <section style={section}>
          <h2 style={heading}>How we use it</h2>
          <p style={paragraph}>
            We use this information to sign you in, save and display your boards, support sharing and collaboration,
            protect accounts, and respond to support or account requests. Essential cookies keep your session active and
            connect a Google sign-in request to the browser that started it. Your browser also stores demo work and view
            preferences locally.
          </p>
        </section>

        <section style={section}>
          <h2 style={heading}>Sharing</h2>
          <p style={paragraph}>
            People you invite can access the boards you share with them. Anyone with a public board link can view that
            board. Google handles the Google sign-in step. Hosting providers process data needed to operate
            Scribbleplot.
          </p>
        </section>

        <section style={section}>
          <h2 style={heading}>Deletion and retention</h2>
          <p style={paragraph}>
            You can delete your account in Settings. This removes your account and boards you own from the active
            service; collaborators then lose access to those boards. Operational backups may retain earlier copies for
            recovery. Contact us if you need help with your data or account.
          </p>
        </section>

        <section style={section}>
          <h2 style={heading}>Contact</h2>
          <p style={paragraph}>
            For privacy questions or requests, email{' '}
            <a href="mailto:low.cameron3@gmail.com" style={{ color: accent }}>
              low.cameron3@gmail.com
            </a>
            .
          </p>
        </section>
      </article>
    </main>
  );
}
